import React, { useState, useCallback } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
  Badge,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AsyncPaginate } from "react-select-async-paginate";
import { authFetch } from "../utils/authFetch";

const LIMIT = 100;

function formatMoney(val) {
  const n = Number(val) || 0;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function RaportCasaZilnic() {
  const today = new Date();
  const yyyy = today.getFullYear();

  const [from, setFrom] = useState(new Date(yyyy, today.getMonth(), 1));
  const [to, setTo] = useState(today);
  const [account, setAccount] = useState(null);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const formatDateString = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const loadAccountOptions = useCallback(async (inputValue, _loaded, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();
    const skip = (page - 1) * LIMIT;
    try {
      const res = await authFetch(
        `/accounts?search=${encodeURIComponent(q)}&skip=${skip}&limit=${LIMIT}&sort_by=code`
      );
      const items = Array.isArray(res)
        ? res
        : Array.isArray(res?.items)
          ? res.items
          : [];
      
      // Prioritize treasury/cash/bank accounts starting with 5 (5311, 5121, 5124 etc.)
      const options = items.map((a) => ({
        value: a.id,
        label: `${a.code} - ${a.name}`,
        code: a.code,
        name: a.name,
      }));

      const hasMore = items.length === LIMIT;

      return {
        options,
        hasMore,
        additional: { page: page + 1 },
      };
    } catch (e) {
      console.error(e);
      return { options: [], hasMore: false, additional: { page } };
    }
  }, []);

  const handleGenerate = async (e) => {
    if (e) e.preventDefault();
    setErr("");
    if (!account) {
      setErr("Te rugăm să selectezi un cont (ex: 5121 Banca ING, 5311 Casa).");
      return;
    }
    if (!from || !to) {
      setErr("Te rugăm să selectezi intervalul de date.");
      return;
    }

    setBusy(true);
    setData(null);

    const fStr = formatDateString(from);
    const tStr = formatDateString(to);

    try {
      const res = await authFetch(
        `/rapoarte/cash-daily?account_id=${account.value}&from=${fStr}&to=${tStr}`
      );
      setData(res);
    } catch (e) {
      console.error(e);
      setErr(e.message || "Eroare la generarea raportului.");
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Container fluid className="p-3">
      {/* Printable CSS style */}
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            body { background: white !important; font-size: 12px; }
            .card { border: none !important; box-shadow: none !important; }
            .table-day { page-break-inside: avoid; }
          }
        `}
      </style>

      <Card className="shadow-sm border-0 rounded-4 p-4 mb-4 bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3 no-print">
          <div>
            <h3 className="fw-bold mb-1 text-dark">Raport Zilnic Casa / Bancă</h3>
            <p className="text-muted mb-0">
              Afișare sold început zi, înregistrări pe zi și sold sfârșit zi pe cont
            </p>
          </div>
          {data && (
            <Button variant="outline-dark" onClick={handlePrint} className="fw-semibold px-3">
              🖨️ Tipărește Raport
            </Button>
          )}
        </div>

        {/* Filter Bar */}
        <Form onSubmit={handleGenerate} className="no-print border-bottom pb-4 mb-3">
          <Row className="g-3 align-items-end">
            <Col md={5} lg={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small text-secondary">
                  Cont Trezorerie / Casa / Bancă
                </Form.Label>
                <AsyncPaginate
                  value={account}
                  loadOptions={loadAccountOptions}
                  onChange={setAccount}
                  placeholder="Caută cont (ex: 5121, 5311)..."
                  isClearable
                  additional={{ page: 1 }}
                />
              </Form.Group>
            </Col>
            <Col md={3} lg={3}>
              <Form.Group>
                <Form.Label className="fw-semibold small text-secondary">
                  De la data
                </Form.Label>
                <DatePicker
                  selected={from}
                  onChange={setFrom}
                  dateFormat="dd.MM.yyyy"
                  className="form-control"
                />
              </Form.Group>
            </Col>
            <Col md={3} lg={3}>
              <Form.Group>
                <Form.Label className="fw-semibold small text-secondary">
                  Până la data
                </Form.Label>
                <DatePicker
                  selected={to}
                  onChange={setTo}
                  dateFormat="dd.MM.yyyy"
                  className="form-control"
                />
              </Form.Group>
            </Col>
            <Col md={1} lg={2}>
              <Button
                type="submit"
                variant="primary"
                className="w-100 fw-semibold shadow-sm"
                disabled={busy}
              >
                {busy ? <Spinner animation="border" size="sm" /> : "Generează"}
              </Button>
            </Col>
          </Row>
        </Form>

        {err && <Alert variant="danger" className="my-3 no-print">{err}</Alert>}

        {/* Report Content */}
        {data && (
          <div>
            {/* Header info */}
            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
              <div>
                <h4 className="fw-bold mb-1 text-primary">
                  {data.account.code} - {data.account.name}
                </h4>
                <div className="text-secondary small">
                  Perioada: <strong>{data.period.from_date}</strong> - <strong>{data.period.to_date}</strong>
                </div>
              </div>
              <div className="text-end">
                <Badge bg="secondary" className="px-3 py-2 fs-6">
                  Tip cont: {data.account.type}
                </Badge>
              </div>
            </div>

            {/* Summary Cards */}
            <Row className="g-3 mb-4">
              <Col md={3}>
                <Card className="border-0 bg-light shadow-sm text-center p-3">
                  <div className="text-muted small fw-semibold">Sold Inițial Perioadă</div>
                  <div className="fs-5 fw-bold text-dark mt-1 font-monospace">
                    {formatMoney(data.opening_balance)} Lei
                  </div>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 bg-success bg-opacity-10 shadow-sm text-center p-3">
                  <div className="text-success small fw-semibold">Total Încasări (Debit)</div>
                  <div className="fs-5 fw-bold text-success mt-1 font-monospace">
                    +{formatMoney(data.total_debit_period)} Lei
                  </div>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 bg-danger bg-opacity-10 shadow-sm text-center p-3">
                  <div className="text-danger small fw-semibold">Total Plăți (Credit)</div>
                  <div className="fs-5 fw-bold text-danger mt-1 font-monospace">
                    -{formatMoney(data.total_credit_period)} Lei
                  </div>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 bg-primary bg-opacity-10 shadow-sm text-center p-3">
                  <div className="text-primary small fw-semibold">Sold Final Perioadă</div>
                  <div className="fs-5 fw-bold text-primary mt-1 font-monospace">
                    {formatMoney(data.closing_balance)} Lei
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Daily Groups */}
            {data.days.length === 0 ? (
              <Alert variant="info" className="text-center py-4">
                Nu există tranzacții înregistrate în perioada selectată pentru acest cont.
              </Alert>
            ) : (
              data.days.map((day, idx) => (
                <Card key={idx} className="mb-4 border shadow-sm table-day rounded-3 overflow-hidden">
                  <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fs-5 fw-bold text-dark">📅 Data: {day.date_formatted}</span>
                    </div>
                    <div className="fs-6 fw-semibold text-secondary">
                      Sold început zi: <span className="font-monospace text-dark fw-bold">{formatMoney(day.sold_start)} Lei</span>
                    </div>
                  </Card.Header>

                  <Card.Body className="p-0">
                    <Table responsive hover size="sm" className="mb-0 align-middle">
                      <thead className="table-secondary text-secondary small">
                        <tr>
                          <th style={{ width: "12%" }}>Nr. Doc</th>
                          <th style={{ width: "18%" }}>Tip Document</th>
                          <th style={{ width: "25%" }}>Partener</th>
                          <th style={{ width: "25%" }}>Explicație</th>
                          <th style={{ width: "10%" }} className="text-end text-success">Încasări (D)</th>
                          <th style={{ width: "10%" }} className="text-end text-danger">Plăți (C)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {day.transactions.map((tx, tIdx) => (
                          <tr key={tIdx}>
                            <td className="fw-semibold">{tx.doc_no}</td>
                            <td>
                              <Badge bg={tx.debit > 0 ? "success" : "danger"} text="white">
                                {tx.tip}
                              </Badge>
                            </td>
                            <td>{tx.partner_name || "-"}</td>
                            <td className="text-muted small">{tx.explicatie || "-"}</td>
                            <td className="text-end font-monospace fw-bold text-success">
                              {tx.debit > 0 ? formatMoney(tx.debit) : "-"}
                            </td>
                            <td className="text-end font-monospace fw-bold text-danger">
                              {tx.credit > 0 ? formatMoney(tx.credit) : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>

                  <Card.Footer className="bg-white d-flex justify-content-between align-items-center py-3 border-top">
                    <div className="d-flex gap-4">
                      <span className="small text-muted">
                        Total încasări zi: <strong className="text-success font-monospace">+{formatMoney(day.total_debit)} Lei</strong>
                      </span>
                      <span className="small text-muted">
                        Total plăți zi: <strong className="text-danger font-monospace">-{formatMoney(day.total_credit)} Lei</strong>
                      </span>
                    </div>
                    <div className="fs-6 fw-bold text-dark">
                      Sold sfârșit zi: <span className="font-monospace text-primary">{formatMoney(day.sold_end)} Lei</span>
                    </div>
                  </Card.Footer>
                </Card>
              ))
            )}
          </div>
        )}
      </Card>
    </Container>
  );
}
